/**
 * Get week dates (Monday to Sunday)
 * @param {Date} selectedDate - Current selected date
 * @returns {Array<Date>} - Array of 7 dates for the week
 */
export const getWeekDates = (selectedDate) => {
    const current = new Date(selectedDate)
    const monday = new Date(current)
    // Get Monday of current week (0 = Sunday, 1 = Monday, etc.)
    const day = current.getDay()
    const diff = day === 0 ? -6 : 1 - day // If Sunday, go back 6 days, otherwise go back to Monday
    monday.setDate(current.getDate() + diff)

    return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(monday)
        date.setDate(monday.getDate() + i)
        return date
    })
}

/**
 * Get month calendar grid (6 weeks x 7 days)
 * @param {Date} selectedDate - Current selected date
 * @returns {Array<Object>} - Array of date objects with metadata
 */
export const getMonthDates = (selectedDate) => {
    const year = selectedDate.getFullYear()
    const month = selectedDate.getMonth()

    // First day of the month
    const firstDay = new Date(year, month, 1)

    // Start from the Monday before or on the first day
    const startDate = new Date(firstDay)
    const day = firstDay.getDay()
    const diff = day === 0 ? -6 : 1 - day // If Sunday, go back 6 days, otherwise go back to Monday
    startDate.setDate(firstDay.getDate() + diff)

    // Generate 6 weeks (42 days)
    const dates = []
    for (let i = 0; i < 42; i++) {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + i)
        dates.push({
            date,
            isCurrentMonth: date.getMonth() === month,
            dayNumber: date.getDate()
        })
    }

    return dates
}

/**
 * Generate time slots for day view (12 AM to 11 PM)
 * @returns {Array<string>} - Array of time slot strings
 */
export const generateTimeSlots = () => {
    return Array.from({ length: 24 }, (_, i) => {
        const hour = i // 0-23 (24-hour format)
        const ampm = hour >= 12 ? "PM" : "AM"
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
        return `${displayHour} ${ampm}`
    })
}

/**
 * Get bookings for a specific day (used in month view)
 * @param {Date} date - Target date
 * @param {Array} assets - Array of assets with bookings
 * @param {string} selectedAsset - Selected asset ID or "all"
 * @returns {Array} - Array of bookings for the day
 */
export const getBookingsForDay = (date, assets, selectedAsset) => {
    const bookingsMap = new Map()

    // Filter assets based on selected asset
    const filteredAssets = selectedAsset === "all"
        ? assets
        : assets.filter((asset) => asset.id === Number.parseInt(selectedAsset))

    filteredAssets.forEach((asset) => {
        asset.bookings?.forEach((booking) => {
            const bookingDate = new Date(booking.date)
            if (
                bookingDate.getDate() === date.getDate() &&
                bookingDate.getMonth() === date.getMonth() &&
                bookingDate.getFullYear() === date.getFullYear()
            ) {
                // Use booking ID as key to avoid duplicates
                // Only add if not already in map (prevents duplicate bookings with multiple assets)
                if (!bookingsMap.has(booking.id)) {
                    bookingsMap.set(booking.id, {
                        ...booking,
                        assetNumber: asset.number
                    })
                }
            }
        })
    })

    return Array.from(bookingsMap.values())
}
